'use client';
import React, { useRef, useEffect } from 'react';
import { ContentfulComponentProps } from '../type';
import { StickyBar, theme } from '@/ui-components/molecules/StickyBar/StickyBar';
import styles from './stickycta.module.scss';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { Button, ButtonProps } from '@/ui-components/atoms/Button/Button';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { PznMessage } from '../PznMessage/PznMessage';
import cn from 'classnames';
import { responsiveBreakpointsPx } from '@/constants';

interface ThemeMap {
    [index: string]: theme;
}

interface StickyCtaBarProps extends ContentfulComponentProps<SiteStickyCta> {
    inFlow?: boolean;
}

export default function StickyCtaBar({ asset, inFlow = false }: StickyCtaBarProps): React.JSX.Element {
    const barRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!inFlow || !barRef.current) return;
        if (window.innerWidth < responsiveBreakpointsPx.md) return;
        if (sessionStorage.getItem('stickyCtaDismissed')) return;

        const bar = barRef.current;
        bar.style.transition = 'none';
        bar.style.height = 'auto';
        const naturalHeight = bar.offsetHeight;
        bar.style.height = '0px';
        void bar.offsetHeight; // force reflow
        bar.style.transition = 'height 0.3s ease-out';
        bar.style.height = `${naturalHeight}px`;
        bar.addEventListener(
            'transitionend',
            () => {
                bar.style.height = 'auto';
                bar.style.transition = '';
            },
            { once: true },
        );
    }, [inFlow]);
    const themeMap: ThemeMap = {
        Black: 'black',
        White: 'white',
        'Brand Blue': 'blue',
    };

    const subheadComponent = asset.fields.subheadline ? <RenderRTE contentfulDoc={asset.fields.subheadline} /> : '';
    const legalComponent = asset.fields.legal_text ? <RenderRTE contentfulDoc={asset.fields.legal_text} /> : '';
    const legal = asset.fields.presentation.styles['Legal Text Alignment'] === 'Below Headline' ? legalComponent : '';
    const caption = asset.fields.presentation.styles['Legal Text Alignment'] === 'Below CTA' ? legalComponent : '';
    const isDismissible = asset.fields.dismiss === 'Yes';

    let img: React.ReactElement<typeof HTMLImageElement> | undefined;

    if (asset.fields.image) {
        // image size optimised based on container
        img = (
            <ImageWrapper
                src={asset.fields.image.url}
                alt={asset.fields.image.altText}
                optimizedWidth={100}
                optimizedHeight={100}
            />
        );
    }

    const ctas = asset.fields.ctas.map((cta, i) => {
        const variant = cta.fields.link_type === 'Primary CTA' ? 'primary' : 'secondary';
        const size = cta.fields.size?.toLowerCase() === 'small' ? 'small' : 'large';

        return (
            <Button
                key={i}
                href={cta.fields.href}
                type="button"
                variant={variant}
                size={size}
                colorSet={cta.fields.colorSet}
                inheritParams={cta.fields.inheritParams}
            >
                {cta.fields.link_text}
            </Button>
        );
    });

    let qrImg: React.ReactElement<typeof HTMLImageElement> | undefined;
    let qrLink: React.ReactElement<ButtonProps> | undefined;

    if (asset.fields.qr_code) {
        const variant = asset.fields.qr_code.fields.link_type === 'Primary CTA' ? 'primary' : 'secondary';

        qrLink = (
            <Button
                href={asset.fields.qr_code.fields.href}
                type="button"
                variant={variant}
                colorSet={asset.fields.qr_code.fields.colorSet}
            >
                {asset.fields.qr_code.fields.link_text}
            </Button>
        );

        if (asset.fields.qr_code.fields.qr_code) {
            // image size optimised based on container
            qrImg = (
                <ImageWrapper
                    src={asset.fields.qr_code.fields.qr_code.url}
                    alt={asset.fields.qr_code.fields.qr_code.altText}
                    optimizedWidth={100}
                    optimizedHeight={100}
                />
            );
        }
    }

    const imageMobile = asset.fields.presentation.styles['Show Mobile Image'] === 'Yes' ? true : false;

    return (
        <div
            ref={barRef}
            className={cn(styles.stickybar, {
                [styles.inFlow]: inFlow,
            })}
        >
            <StickyBar
                leadImage={img}
                headline={
                    asset.fields.pzn_headline ? (
                        <PznMessage
                            template={asset.fields.pzn_headline}
                            fallback={asset.fields.headline}
                            fnFallback={asset.fields.pzn_headline_fn}
                        />
                    ) : (
                        asset.fields.headline
                    )
                }
                subhead={subheadComponent}
                legal={legal}
                ctaCaption={caption}
                ctas={ctas}
                qrcode={qrImg}
                qrMobileLink={qrLink}
                theme={themeMap[asset.fields.background_color]}
                imageMobile={imageMobile}
                dismissible={isDismissible}
            />
        </div>
    );
}
